import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Select, Switch } from '@/components/ui';
import { money } from '@/lib/format';
import { getProspectSetup, startProspectSearch } from '@/data/client';
import type { ProspectSearchInput, ProspectSetup } from '@/data/types';
import { FormError } from '@/components/crm/Modal';
import { US_STATE_NAMES } from './states';

const DEFAULTS = {
  radiusMiles: 25, maxBusinesses: 100, minReviews: 25, minRating: 3.8, requireClaimed: true, fromPos: 11, toPos: 30, minInRange: 1,
  maxPage1: 2, requireAdSignal: true, minPaidValue: 0, excludeMulti: true, multiThreshold: 4, checkTransparency: true, maxCost: 3,
} satisfies Partial<ProspectSearchInput>;

/* The Find tab: two separate searches, one card each, so it is obvious which
   lookup is which. "By category" is the sheet's Find Prospects; "by search
   term" starts from what people type into Google and maps each business Google
   Maps shows to a Setup category. Both run in the background; results land in
   the call queue and the run's details explain every business skipped. */
export function FindPanel({ onStarted }: { onStarted: () => void }) {
  const { data: setup } = useQuery({ queryKey: ['prospectSetup'], queryFn: getProspectSetup });
  return (
    <div className="crm-list">
      {setup && !setup.configured && (
        <Card padding={false} className="crm-section">
          <p className="crm-callout">DataForSEO isn't connected yet. Add <code>DATAFORSEO_LOGIN</code> and <code>DATAFORSEO_PASSWORD</code> (DataForSEO dashboard → API Access) to the Worker's secrets.</p>
        </Card>
      )}
      <FindByCategory setup={setup} onStarted={onStarted} />
      <FindByTerm setup={setup} onStarted={onStarted} />
    </div>
  );
}

/** Form state, field helpers and the start mutation, the same for both cards. */
function useSearchForm(initial: ProspectSearchInput, onStarted: () => void) {
  const qc = useQueryClient();
  const [f, setF] = useState<ProspectSearchInput>(initial);
  const num = (k: keyof ProspectSearchInput) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value === '' ? undefined : Number(e.target.value) }));
  const flag = (k: keyof ProspectSearchInput) => (e: { target: { checked: boolean } }) => setF((x) => ({ ...x, [k]: e.target.checked }));
  const text = (k: keyof ProspectSearchInput) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const start = useMutation({
    mutationFn: () => startProspectSearch(f),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['prospectRuns'] }); onStarted(); },
  });
  return { f, num, flag, text, start };
}

const budgetLeft = (setup?: ProspectSetup) => (setup ? Math.max(0, setup.monthlyBudget - setup.spentThisMonth) : null);

function FindByCategory({ setup, onStarted }: { setup?: ProspectSetup; onStarted: () => void }) {
  const { f, num, flag, text, start } = useSearchForm({ mode: 'category', city: '', state: 'Texas', categoryId: '', ...DEFAULTS }, onStarted);
  const category = setup?.categories.find((c) => c.id === f.categoryId);
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head">
        <h3>Find by category</h3>
        {setup && <span className="crm-cell-sub">{money(setup.spentThisMonth)} of {money(setup.monthlyBudget)} used this month</span>}
      </div>
      <p className="crm-cell-sub">The businesses Google lists under a category in the area, checked against that category's keywords.</p>
      <div className="crm-form">
        <div className="crm-grid2">
          <Input label="City" placeholder="Dallas" value={f.city} onChange={text('city')} />
          <Select label="State" options={US_STATE_NAMES.map((s) => ({ value: s, label: s }))} value={f.state} onChange={text('state')} />
          <Select label="Category" placeholder="Choose…" options={(setup?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={f.categoryId ?? ''} onChange={text('categoryId')} />
          <Input label="Radius (miles)" type="number" value={f.radiusMiles ?? ''} onChange={num('radiusMiles')} />
        </div>
        {category && (
          <p className="crm-cell-sub">
            Checks {category.keywords.filter((k) => k.active).length} keywords: {category.keywords.filter((k) => k.active).map((k) => k.keyword.replace(/\{city\}/gi, f.city || '{city}')).join(' · ')}
          </p>
        )}
        <FilterFields f={f} num={num} flag={flag} left={budgetLeft(setup)} />
        <SkipNote />
        <div><Button onClick={() => start.mutate()} disabled={start.isPending || !f.city || !f.categoryId || setup?.configured === false}>{start.isPending ? 'Starting…' : 'Find by category'}</Button></div>
        <FormError error={start.error} />
      </div>
    </Card>
  );
}

function FindByTerm({ setup, onStarted }: { setup?: ProspectSetup; onStarted: () => void }) {
  const { f, num, flag, text, start } = useSearchForm({ mode: 'term', city: '', state: 'Texas', term: '', providerFilter: 'any', ...DEFAULTS }, onStarted);
  const providers = setup?.providers ?? [];
  const searchable = (setup?.categories ?? []).filter((c) => c.googleIds.trim() && c.keywords.some((k) => k.active)).length;
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head">
        <h3>Find by search term</h3>
        <span className="crm-cell-sub">A different lookup: starts from a Google search, not a category</span>
      </div>
      <p className="crm-cell-sub">
        The businesses Google Maps shows for a search around the city. Each one is matched to a Setup category by its Google category and
        checked against that category's keywords plus the term itself. Businesses with no matching Setup category are listed in the search details, not added.
      </p>
      <div className="crm-form">
        <div className="crm-grid2">
          <Input label="Search term" placeholder="ac repair dallas" value={f.term ?? ''} onChange={text('term')} hint="Exactly as someone would type it into Google" />
          <Input label="Radius (miles)" type="number" value={f.radiusMiles ?? ''} onChange={num('radiusMiles')} />
          <Input label="City" placeholder="Dallas" value={f.city} onChange={text('city')} />
          <Select label="State" options={US_STATE_NAMES.map((s) => ({ value: s, label: s }))} value={f.state} onChange={text('state')} />
          <Select label="Website provider" value={f.providerFilter ?? 'any'} onChange={text('providerFilter')}
            options={[
              { value: 'any', label: 'Any website provider' },
              ...(providers.length ? [{ value: 'known', label: 'Only sites with a known provider tagline' }] : []),
              ...providers.map((p) => ({ value: p.id, label: `Only ${p.name} sites` })),
            ]}
            hint={providers.length ? 'Read from each site\'s homepage (Setup → Website providers)' : 'Add providers in Setup to filter by who built the site'} />
        </div>
        {setup && <p className="crm-cell-sub">Matches against {searchable} Setup {searchable === 1 ? 'category' : 'categories'} with Google ids and keywords.</p>}
        <FilterFields f={f} num={num} flag={flag} left={budgetLeft(setup)} maxBusinessesMax={200} />
        <SkipNote />
        <div><Button onClick={() => start.mutate()} disabled={start.isPending || !f.city || !(f.term ?? '').trim() || setup?.configured === false}>{start.isPending ? 'Starting…' : 'Find by search term'}</Button></div>
        <FormError error={start.error} />
      </div>
    </Card>
  );
}

const SkipNote = () => (
  <p className="crm-cell-sub">Skipped automatically: existing clients, companies with an open deal, Do Not Contact, excluded domains, and anyone already prospected — earlier contacts are listed in the search details with what happened.</p>
);

/** The sheet's filters (reviews, ranking range, ads, cost), collapsed by default. */
function FilterFields({ f, num, flag, left, maxBusinessesMax = 300 }: {
  f: ProspectSearchInput;
  num: (k: keyof ProspectSearchInput) => (e: { target: { value: string } }) => void;
  flag: (k: keyof ProspectSearchInput) => (e: { target: { checked: boolean } }) => void;
  left: number | null;
  maxBusinessesMax?: number;
}) {
  const [advanced, setAdvanced] = useState(false);
  return (
    <>
      <button type="button" className="crm-linkbtn" onClick={() => setAdvanced((a) => !a)}>{advanced ? 'Hide filters' : 'Filters (reviews, ranking range, ads, cost)'}</button>
      {advanced && (
        <>
          <div className="crm-grid2">
            <Input label="Max businesses to check" type="number" hint={`Largest by review count first. Max ${maxBusinessesMax}.`} value={f.maxBusinesses ?? ''} onChange={num('maxBusinesses')} />
            <Input label="Min Google reviews" type="number" value={f.minReviews ?? ''} onChange={num('minReviews')} />
            <Input label="Min rating" type="number" step="0.1" value={f.minRating ?? ''} onChange={num('minRating')} />
            <Input label="Max page-1 keywords allowed" type="number" hint="0 = strict" value={f.maxPage1 ?? ''} onChange={num('maxPage1')} />
            <Input label="Ranking range: from" type="number" hint="Page 2 starts at 11" value={f.fromPos ?? ''} onChange={num('fromPos')} />
            <Input label="Ranking range: to" type="number" hint="Page 3 ends at 30. Max 50." value={f.toPos ?? ''} onChange={num('toPos')} />
            <Input label="Min keywords in range" type="number" value={f.minInRange ?? ''} onChange={num('minInRange')} />
            <Input label="Min paid traffic value $/mo" type="number" hint="DataForSEO estimate; 0 = any" value={f.minPaidValue ?? ''} onChange={num('minPaidValue')} />
            <Input label="Multi-location threshold" type="number" hint="Listings in the area that make it a chain" value={f.multiThreshold ?? ''} onChange={num('multiThreshold')} />
            <Input label="Max cost for this search ($)" type="number" step="0.5" hint={left != null ? `${money(left)} left this month` : undefined} value={f.maxCost ?? ''} onChange={num('maxCost')} />
          </div>
          <Switch label="Require a claimed Google profile" checked={!!f.requireClaimed} onChange={flag('requireClaimed')} />
          <Switch label="Require an ad signal (live ad, paid keywords or recent Ads Transparency)" checked={!!f.requireAdSignal} onChange={flag('requireAdSignal')} />
          <Switch label="Skip multi-location brands" checked={!!f.excludeMulti} onChange={flag('excludeMulti')} />
          <Switch label="Check Google Ads Transparency (finishes a few minutes later)" checked={!!f.checkTransparency} onChange={flag('checkTransparency')} />
        </>
      )}
    </>
  );
}
